import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';

import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import { Icon } from '../icon';
import { SelectionBox } from './components/bounding-box-selector';
import { CloseIcon } from './components/close-icon';
import { DocViewerLoader } from './components/doc-viewer-loader';
import { DownloadIcon, ThreadFileDownloadContext } from './components/download-icon';
import { ExtractFactFromImagePopup } from './components/extract-fact-from-image-popup';
import { ExtractFactsFromSelectionButton } from './components/extract-facts-from-selection-button';
import { FullDocLink } from './components/full-doc-link';
import { ImageRenderer } from './components/image-renderer';
import { PDFDocumentRenderer } from './components/pdf-renderer';
import { SearchBar } from './components/search-bar';
import { SearchButton } from './components/search-button';
import { ShowFactsIcon } from './components/show-facts';
import { Zoom } from './components/zoom';
import { DEFAULT_PAGE_HEIGHT, DEFAULT_PAGE_WIDTH } from './constants';
import { findPageMatches, matchAnchorBox, normaliseSearchQuery, WordMatch } from './text-matching';
import { useVisiblePages } from './use-visible-pages';
import { TrackingEventsV2 } from '../../../analytics/eventsV2';
import { trackCustomEvent } from '../../../analytics/Mixpanel';
import { Button } from '../shadcn/button';
import { ButtonGroup } from '../shadcn/button-group';
import { useGetDoc } from '@/api/queries/useGetDoc';
import { useGetDocChunkMatchesV2 } from '@/api/queries/useGetDocChunkMatchesV2';
import { useLazyFetchDocPageText, useBatchGetDocPagesText } from '@/api/queries/useGetDocPageText';
import { resolveViewerPageCount } from '@/new/lib/doc-viewer-utils';
import { BoundingBox, BoundingBoxWithId } from '@/types';

// When a text-layer search matches more than this many pages we switch from
// eager mode (fetch every matched page's text upfront for an exact count) to
// lazy mode (fetch one page at a time as the user steps through results, showing
// the total as '?'). Keeps massive searches fast and memory-bounded.
const EAGER_SEARCH_PAGE_LIMIT = 50;

// Smooth-scroll only for jumps within this many viewport heights. A longer jump
// (e.g. a search match far across the document) is done instantly — otherwise
// the smooth animation sweeps through every page in between, mounting each one
// and spamming the image / page-text endpoints.
const SMOOTH_SCROLL_MAX_VIEWPORTS = 3;

// When navigating to a highlight, auto-zoom so small highlights are readable
// straight away: pick the zoom at which the highlight fills these shares of
// the viewport
const HIGHLIGHT_ZOOM_MAX = 3;
const HIGHLIGHT_ZOOM_WIDTH_SHARE = 0.85;
const HIGHLIGHT_ZOOM_HEIGHT_SHARE = 0.5;
// Skip zoom adjustments smaller than this fraction of the current zoom
const HIGHLIGHT_ZOOM_MIN_CHANGE = 0.1;
// Share of the leftover viewport height placed above the highlight when scrolling to it
const HIGHLIGHT_VIEWPORT_TOP_SHARE = 0.35;

// A single search match located via lazy navigation (the next/previous result
// is resolved on demand rather than from a precomputed list).
interface LazySearchMatch {
  // 1-based position in document order. Exact because lazy navigation only ever
  // moves ±1 from the first match, so each step is the true next/previous match.
  ordinal: number;
  // Index into candidateSearchPageIndices of the page this match is on.
  pageListIndex: number;
  // Index of the match on its page, in findPageMatches (document) order.
  matchIndexOnPage: number;
  pageNumber: number;
  // Normalised (0..1) top of the matched word, for scroll targeting.
  top: number;
}

const countOccurrences = (chunk: string, query: string): number => {
  if (!query) return 0;
  let count = 0;
  let from = chunk.indexOf(query);
  while (from !== -1) {
    count += 1;
    from = chunk.indexOf(query, from + query.length);
  }
  return count;
};

export interface RendererBaseProps {
  docId: string;

  getPageDimensions: (pageNumber: number) => { height: number; width: number };
  visiblePages: number[];
  getPagePosition: (pageNumber: number) => number;
  totalHeight: number;
  maxPageWidth?: number;

  highlights: BoundingBoxWithId[];
  highlightedFactId?: string | null;
  onHighlightClick?: (id: string) => void;
  highlightedChunkId?: string | null;
  searchQuery?: string;
  focusedMatch?: { pageNumber: number; indexOnPage: number } | null;
  isSelectionMode?: boolean;
  onSelectionComplete?: (
    boundingBox: BoundingBox,
    imageData: string,
    selectionBox: SelectionBox,
    pageNumber: number,
  ) => void;
  maintainSelection?: boolean;
  activeSelection?: { box: SelectionBox; pageNumber: number } | null;
}

export interface PDFViewerRef {
  clearSearch: () => void;
}

interface PDFViewerProps {
  docId: string;
  highlights: BoundingBoxWithId[];
  setHighlightedFactId?: (id: string) => void;
  highlightedFactId?: string | null;
  onHighlightClick?: (id: string) => void;
  onViewerClick?: () => void;
  closeViewer?: () => void;
  hideFeatureButtons?: boolean;
  hideSnippingTool?: boolean;
  hideDownload?: boolean;
  hideFactInfo?: boolean;
  showFacts?: boolean;
  setShowFacts?: (show: boolean) => void;
  fileName?: string;
  initialSearchQuery?: string;
  fileUrl?: string;
  fileMetadata?: {
    name?: string;
    extension?: string;
    numberOfPages?: number | null;
    height?: number | null;
    width?: number | null;
    hasBeenConvertedToImages?: boolean;
    hasTextLayer?: boolean;
    pageDimensions?: { width: number; height: number }[] | null;
  };
  threadFile?: ThreadFileDownloadContext;
}

export const PDFViewer = forwardRef<PDFViewerRef, PDFViewerProps>(
  (
    {
      docId,
      highlights,
      closeViewer,
      highlightedFactId,
      setHighlightedFactId,
      onHighlightClick,
      onViewerClick,
      hideFeatureButtons,
      hideSnippingTool,
      hideDownload,
      hideFactInfo,
      showFacts,
      setShowFacts,
      fileName,
      initialSearchQuery,
      fileUrl,
      fileMetadata,
      threadFile,
    },
    ref,
  ) => {
    const { matterId } = useParams();

    // Selection mode state
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [popupData, setPopupData] = useState<{
      isOpen: boolean;
      imageBase64: string;
      boundingBox: BoundingBox | null;
      anchorPosition: { x: number; y: number; pageNumber: number } | null;
      side: 'left' | 'right';
    }>({
      isOpen: false,
      imageBase64: '',
      boundingBox: null,
      anchorPosition: null,
      side: 'left',
    });
    const [activeSelection, setActiveSelection] = useState<{ box: SelectionBox; pageNumber: number } | null>(null);

    // Search state
    const [searchQuery, setSearchQuery] = useState(initialSearchQuery || '');
    const [showSearchBar, setShowSearchBar] = useState(!!initialSearchQuery);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
    const [highlightedChunkId, setHighlightedChunkId] = useState<string | null>(null);
    const [focusedMatch, setFocusedMatch] = useState<{ pageNumber: number; indexOnPage: number } | null>(null);
    const [lazyMatch, setLazyMatch] = useState<LazySearchMatch | null>(null);
    const [isNavigatingLazy, setIsNavigatingLazy] = useState(false);
    const hasUserNavigatedRef = useRef(false);

    // Zoom state - start with 1, will be updated when doc loads
    const [zoom, setZoom] = useState(1);
    const isUpdatingZoom = useRef(false);
    const zoomRef = useRef(1);
    const pendingScrollRef = useRef<{ top: number; left: number } | null>(null);
    const pendingAutoZoomLayoutRef = useRef(false);
    const pendingAutoZoomHighlightSyncRef = useRef(false);
    const hasFinishedInitialLayoutRef = useRef(false);
    const resizeAutoZoomTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastObservedContainerWidthRef = useRef<number | null>(null);

    // Outer container
    const containerRef = useRef<HTMLDivElement>(null);

    // Get info about the doc
    const [loadedFileUrlPageCount, setLoadedFileUrlPageCount] = useState<number | null>(null);
    const [loadedPdfPageDimensions, setLoadedPdfPageDimensions] = useState<
      Record<number, { width: number; height: number }>
    >({});

    const shouldFetchDoc = !fileUrl;
    const { data: doc, isLoading: isFetchedDocLoading } = useGetDoc(shouldFetchDoc ? matterId : undefined, docId);
    const file = fileMetadata ?? doc?.file ?? (fileUrl ? {} : undefined);
    const hasBeenConvertedToImages = file?.hasBeenConvertedToImages ?? false;
    const useTextLayer = hasBeenConvertedToImages && (file?.hasTextLayer ?? false);
    const numPages = resolveViewerPageCount({
      extension: file?.extension,
      numberOfPages: file?.numberOfPages,
      loadedPdfPageCount: loadedFileUrlPageCount,
      pageDimensionsCount: file?.pageDimensions?.length,
    });
    const isDocLoading = shouldFetchDoc && isFetchedDocLoading;
    const hasLoadedPdfPageDimensions = Object.keys(loadedPdfPageDimensions).length > 0;

    // Get search matches
    const { data: searchMatches, isFetching: isChunkMatchesFetching } = useGetDocChunkMatchesV2(
      matterId,
      docId,
      searchQuery,
      // Disable search matches when using a file URL or if matter, doc, or search query not set
      !!matterId && !!docId && !!searchQuery && searchQuery.trim() !== '' && !fileUrl,
    );

    // Combine fact highlights with search highlights
    const { allHighlights, searchChunks } = useMemo(() => {
      // When searching, only show search highlights
      const combinedHighlights = searchQuery ? [] : [...highlights];
      const chunks: { chunkId: string; boundingBoxIds: string[]; content: string }[] = [];

      if (searchMatches?.chunks) {
        searchMatches.chunks.forEach((chunk) => {
          const chunkBoundingBoxIds: string[] = [];
          // We don't need to show the chunk bounding boxes for docs which have a text layer
          // only keep these highlights for docs which don't have a text layer
          if (chunk.boundingBoxes && !useTextLayer) {
            chunk.boundingBoxes.forEach((box, boxIndex) => {
              const searchId = `search-${chunk.id}-${box.pageNumber}-${boxIndex}`;
              chunkBoundingBoxIds.push(searchId);
              combinedHighlights.push({
                ...box,
                id: searchId,
              });
            });
          }
          if (chunkBoundingBoxIds.length > 0) {
            chunks.push({ chunkId: chunk.id, boundingBoxIds: chunkBoundingBoxIds, content: chunk.content });
          }
        });
      }

      return { allHighlights: combinedHighlights, searchChunks: chunks };
    }, [highlights, searchMatches, searchQuery, useTextLayer]);

    const searchNav = useMemo(() => {
      const query = searchQuery.trim().toLowerCase().replace(/\s+/g, ' ');
      if (!query || searchChunks.length === 0) {
        return { occurrences: [] as typeof searchChunks };
      }

      const occurrences: typeof searchChunks = [];
      searchChunks.forEach((chunk) => {
        const count = countOccurrences((chunk.content ?? '').toLowerCase().replace(/\s+/g, ' '), query);
        if (count > 0) {
          for (let i = 0; i < count; i += 1) occurrences.push(chunk);
        }
      });

      return { occurrences };
    }, [searchQuery, searchChunks]);

    // One entry per real phrase occurrence in a chunk; the PDF (non-image)
    // viewer navigates these, scrolling to the chunk a match belongs to.
    const matchOccurrences = searchNav.occurrences;

    // --- Word-level search navigation ---
    // For image docs the text layer highlights the real OCR word matches, so
    // navigation steps through those exact word positions rather than the chunk
    // a match sits in. The chunk matches are used only to find which pages
    // contain a match; we then fetch those pages' OCR and locate every word
    // match on them so we can scroll to the word.
    const candidateSearchPageIndices = useMemo(() => {
      if (!useTextLayer) return [];
      const pages = new Set<number>();
      searchMatches?.chunks?.forEach((chunk) => {
        chunk.boundingBoxes?.forEach((box) => {
          if (box.pageNumber >= 1) pages.add(box.pageNumber - 1);
        });
      });
      return Array.from(pages).sort((a, b) => a - b);
    }, [useTextLayer, searchMatches]);

    // Beyond the eager limit we don't fetch every matched page upfront — search
    // navigation becomes lazy (one page at a time) and the total shows as '?'.
    const isLazySearch = useTextLayer && candidateSearchPageIndices.length > EAGER_SEARCH_PAGE_LIMIT;

    // Eager mode fetches all matched pages at once for an exact count + instant
    // navigation. Disabled in lazy mode (we fetch on demand instead).
    const { pagesText: candidatePagesText, isFetching: isPagesTextFetching } = useBatchGetDocPagesText(
      matterId,
      docId,
      isLazySearch ? [] : candidateSearchPageIndices,
    );

    // Imperative single-page fetch for lazy navigation (and prefetch).
    const fetchLazyPageText = useLazyFetchDocPageText(matterId, docId);

    const wordMatches = useMemo<WordMatch[]>(() => {
      if (!useTextLayer || isLazySearch) return [];
      const query = normaliseSearchQuery(searchQuery);
      if (!query) return [];

      const matches: WordMatch[] = [];
      candidateSearchPageIndices.forEach((pageIndex) => {
        const lines = candidatePagesText[pageIndex];
        if (!lines) return;
        findPageMatches(lines, query).forEach((match, indexOnPage) => {
          const box = matchAnchorBox(lines, match);
          if (box) matches.push({ pageNumber: pageIndex + 1, indexOnPage, top: box.top });
        });
      });

      // Word matches order: pages, then top-to-bottom within a page, then index (so left to right).
      matches.sort((a, b) => a.pageNumber - b.pageNumber || a.top - b.top || a.indexOnPage - b.indexOnPage);
      return matches;
    }, [useTextLayer, isLazySearch, candidateSearchPageIndices, candidatePagesText, searchQuery]);

    // Navigation steps through word matches (eager text-layer) or chunk
    // occurrences (no text layer). Lazy mode uses its own cursor (lazyMatch).
    const totalSearchMatches = useTextLayer ? wordMatches.length : matchOccurrences.length;

    // The counter shown in the search bar.
    const matchCounter = useMemo(() => {
      // Lazy mode: exact ordinal we've stepped to, but an unknown total ('?').
      if (isLazySearch) {
        return {
          hasResults: candidateSearchPageIndices.length > 0,
          current: lazyMatch?.ordinal ?? 0,
          total: null as number | null,
        };
      }

      if (totalSearchMatches === 0) {
        return { hasResults: false, current: 0, total: 0 };
      }

      const safeIndex = Math.min(currentMatchIndex, totalSearchMatches - 1);
      return {
        hasResults: true,
        current: safeIndex + 1,
        total: totalSearchMatches,
      };
    }, [
      isLazySearch,
      candidateSearchPageIndices,
      lazyMatch,
      searchNav,
      totalSearchMatches,
      currentMatchIndex,
      useTextLayer,
    ]);

    const isSearchLoading =
      !!searchQuery && (isChunkMatchesFetching || (useTextLayer && !isLazySearch && isPagesTextFetching));

    // Reset current match index when search results change
    useEffect(() => {
      if (searchQuery) {
        setCurrentMatchIndex(0);
        setHighlightedChunkId(null);
        setFocusedMatch(null);
        setLazyMatch(null);
        setIsNavigatingLazy(false);
        hasUserNavigatedRef.current = false;
      }
    }, [searchMatches]);

    // Track current index when navigating through a fact's multiple page highlights
    const [currentFactHighlightIndex, setCurrentFactHighlightIndex] = useState(0);

    // Compute sorted unique highlights for the currently highlighted fact
    const factHighlightsForCurrentFact = useMemo(() => {
      if (!highlightedFactId) return [];

      // Get all highlights for this fact, sorted by page then by top position
      const factHighlights = allHighlights
        .filter((h) => h.id === highlightedFactId)
        .sort((a, b) => {
          const pageCompare = a.pageNumber - b.pageNumber;
          if (pageCompare !== 0) return pageCompare;
          return a.top - b.top;
        });

      return factHighlights;
    }, [highlightedFactId, allHighlights]);

    // Get unique pages for the current fact (for display purposes)
    const uniquePagesForCurrentFact = useMemo(() => {
      const pages = new Set(factHighlightsForCurrentFact.map((h) => h.pageNumber));
      return Array.from(pages).sort((a, b) => a - b);
    }, [factHighlightsForCurrentFact]);

    const hasHighlightedFact = useMemo(
      () => (highlightedFactId ? allHighlights.some((highlight) => highlight.id === highlightedFactId) : false),
      [allHighlights, highlightedFactId],
    );

    // Reset highlight index when fact changes
    useEffect(() => {
      setCurrentFactHighlightIndex(0);
    }, [highlightedFactId]);

    // Calculate max width across all pages for default zoom
    const getMaxPageWidth = useCallback(() => {
      if (!file || numPages === 0) return DEFAULT_PAGE_WIDTH;

      let maxWidth = 0;

      if (file.pageDimensions) {
        for (let i = 0; i < numPages; i++) {
          const pageDim = file.pageDimensions[i];
          if (pageDim) {
            const scaleRatio = pageDim.height / DEFAULT_PAGE_HEIGHT;
            const normalizedWidth = pageDim.width / scaleRatio;
            maxWidth = Math.max(maxWidth, normalizedWidth);
          }
        }
      } else if (hasLoadedPdfPageDimensions) {
        for (let i = 1; i <= numPages; i++) {
          const pageDim = loadedPdfPageDimensions[i];
          if (pageDim) {
            maxWidth = Math.max(maxWidth, pageDim.width);
          }
        }
      } else {
        const scaleRatio = (file.height ?? DEFAULT_PAGE_HEIGHT) / DEFAULT_PAGE_HEIGHT;
        maxWidth = (file.width ?? DEFAULT_PAGE_WIDTH) / scaleRatio;
      }

      return maxWidth || DEFAULT_PAGE_WIDTH;
    }, [file, numPages, hasLoadedPdfPageDimensions, loadedPdfPageDimensions]);

    // Page dimensions at zoom 1. Kept separate from the zoomed dimensions so
    // zoom-independent layers (image, text layer, highlight canvas) can render
    // once at this size and be scaled with a single CSS transform, instead of
    // re-laying-out every element on each zoom tick.
    const getBasePageDimensions = useCallback(
      (pageNumber: number) => {
        if (file?.pageDimensions && file.pageDimensions[pageNumber - 1]) {
          const pageDim = file.pageDimensions[pageNumber - 1];
          const scaleRatio = pageDim.height / DEFAULT_PAGE_HEIGHT;
          return {
            height: DEFAULT_PAGE_HEIGHT,
            width: pageDim.width / scaleRatio,
          };
        }

        const loadedPageDim = loadedPdfPageDimensions[pageNumber];
        if (loadedPageDim) {
          return {
            height: loadedPageDim.height,
            width: loadedPageDim.width,
          };
        }

        const scaleRatio = (file?.height ?? DEFAULT_PAGE_HEIGHT) / DEFAULT_PAGE_HEIGHT;
        return {
          height: DEFAULT_PAGE_HEIGHT,
          width: (file?.width ?? DEFAULT_PAGE_WIDTH) / scaleRatio,
        };
      },
      [file, loadedPdfPageDimensions],
    );

    const getPageDimensions = useCallback(
      (pageNumber: number) => {
        const base = getBasePageDimensions(pageNumber);
        return {
          height: base.height * zoom,
          width: base.width * zoom,
        };
      },
      [getBasePageDimensions, zoom],
    );

    // Precompute page positions, total height, and max width in a single pass
    const { totalHeight, maxPageWidth, pagePositions } = useMemo(() => {
      const positions: number[] = [0]; // positions[0] is unused, positions[1] = position of page 1
      let height = 0;
      let maxWidth = 0;

      for (let i = 1; i <= numPages; i++) {
        positions[i] = height;
        const pageDims = getPageDimensions(i);
        height += pageDims.height + 10; // 10px gap between pages
        maxWidth = Math.max(maxWidth, pageDims.width);
      }

      return { totalHeight: height, maxPageWidth: maxWidth, pagePositions: positions };
    }, [numPages, getPageDimensions]);

    // Cumulative page tops at zoom 1, excluding the fixed 10px inter-page gaps
    // (which don't scale with zoom). Highlight navigation computes scroll
    // offsets from these so the maths never depends on the currently rendered
    // zoom — pagePositions and zoomRef can briefly disagree mid zoom change.
    const basePageOffsets = useMemo(() => {
      const offsets: number[] = [0]; // offsets[0] is unused, offsets[1] = top of page 1
      let height = 0;
      for (let i = 1; i <= numPages; i++) {
        offsets[i] = height;
        height += getBasePageDimensions(i).height;
      }
      return offsets;
    }, [numPages, getBasePageDimensions]);

    const getOptimalZoom = useCallback(() => {
      if (!containerRef.current || !file) return 1;

      const container = containerRef.current;
      const availableWidth = container.clientWidth - 40; // Leave some padding
      const maxWidth = getMaxPageWidth();
      const calculatedZoom = availableWidth / maxWidth;
      const finalZoom = Math.min(calculatedZoom, 4); // Cap at 4x zoom

      return finalZoom;
    }, [getMaxPageWidth, file]);

    useEffect(() => {
      zoomRef.current = zoom;
    }, [zoom]);

    const calculateViewportPreservingScroll = useCallback(
      (prevZoom: number, nextZoom: number) => {
        const container = containerRef.current;
        if (!container) return null;

        const viewportCenterY = container.scrollTop + container.clientHeight / 2;
        const viewportCenterX = container.scrollLeft + container.clientWidth / 2;

        // Keep the same point in the document centered, while accounting for
        // the fixed 10px inter-page gaps that do not scale with zoom.
        const pageHeightWithGap = DEFAULT_PAGE_HEIGHT * prevZoom + 10;
        const approxPagePosition = viewportCenterY / pageHeightWithGap;
        const gapsAbove = Math.floor(approxPagePosition);
        const totalGapHeight = gapsAbove * 10;
        const contentY = viewportCenterY - totalGapHeight;
        const normalizedContentY = contentY / prevZoom;

        const newContentY = normalizedContentY * nextZoom;
        const newScrollTop = newContentY + totalGapHeight - container.clientHeight / 2;

        const prevContentWidth = Math.max(getMaxPageWidth() * prevZoom + 40, container.clientWidth);
        const nextContentWidth = Math.max(getMaxPageWidth() * nextZoom + 40, container.clientWidth);
        const viewportOffsetFromCenter = viewportCenterX - prevContentWidth / 2;
        const newViewportCenterX = nextContentWidth / 2 + viewportOffsetFromCenter * (nextZoom / prevZoom);
        const newScrollLeft = newViewportCenterX - container.clientWidth / 2;

        return { top: Math.max(0, newScrollTop), left: Math.max(0, newScrollLeft) };
      },
      [getMaxPageWidth],
    );

    const applyAutoZoom = useCallback(
      (targetZoom: number) => {
        const currentZoom = zoomRef.current;
        const zoomChanged = Math.abs(targetZoom - currentZoom) > 0.001;

        if (!zoomChanged) return;

        pendingAutoZoomLayoutRef.current = true;
        if (hasFinishedInitialLayoutRef.current) {
          pendingScrollRef.current = calculateViewportPreservingScroll(currentZoom, targetZoom);
          pendingAutoZoomHighlightSyncRef.current = Boolean(highlightedFactId);
        }

        isUpdatingZoom.current = true;
        setZoom(targetZoom);

        // Prevent a resize->zoom->resize feedback loop while the DOM settles.
        setTimeout(() => {
          isUpdatingZoom.current = false;
        }, 100);
      },
      [calculateViewportPreservingScroll, highlightedFactId],
    );

    const scheduleAutoZoom = useCallback(
      (delayMs: number) => {
        if (resizeAutoZoomTimeoutRef.current) {
          clearTimeout(resizeAutoZoomTimeoutRef.current);
        }

        resizeAutoZoomTimeoutRef.current = setTimeout(() => {
          resizeAutoZoomTimeoutRef.current = null;
          applyAutoZoom(getOptimalZoom());
        }, delayMs);
      },
      [applyAutoZoom, getOptimalZoom],
    );

    // Latest scheduleAutoZoom for long-lived consumers (ResizeObserver, the
    // once-per-document init effect). Its identity changes whenever a page's
    // dimensions load mid-scroll, and effects depending on it directly would
    // re-run then and re-apply auto zoom while the user scrolls.
    const scheduleAutoZoomRef = useRef(scheduleAutoZoom);
    useEffect(() => {
      scheduleAutoZoomRef.current = scheduleAutoZoom;
    }, [scheduleAutoZoom]);

    const handleZoom = useCallback(
      (delta: number) => {
        if (!containerRef.current) return;

        const prevZoom = zoom;
        // Calculate new zoom using multiplicative scaling for consistent feel at all zoom levels
        // delta is treated as a percentage: positive = zoom in, negative = zoom out
        const zoomFactor = 1 + delta;
        const newZoom = Math.max(0.2, Math.min(zoom * zoomFactor, 3));
        pendingScrollRef.current = calculateViewportPreservingScroll(prevZoom, newZoom);
        setZoom(newZoom);
      },
      [calculateViewportPreservingScroll, zoom],
    );

    const handleZoomIn = () => handleZoom(0.1);
    const handleZoomOut = () => handleZoom(-0.1);

    const handlePdfPageLoadSuccess = useCallback(
      (pageNumber: number, dimensions: { width: number; height: number }) => {
        if (file?.pageDimensions?.[pageNumber - 1]) return;

        setLoadedPdfPageDimensions((prev) => {
          const existing = prev[pageNumber];
          if (
            existing &&
            Math.abs(existing.width - dimensions.width) < 0.01 &&
            Math.abs(existing.height - dimensions.height) < 0.01
          ) {
            return prev;
          }

          return { ...prev, [pageNumber]: dimensions };
        });
      },
      [file?.pageDimensions],
    );

    // Update zoom when document loads and container resizes, but only if user hasn't manually changed it
    const handleResize = useCallback((entries: ResizeObserverEntry[]) => {
      const container = containerRef.current;

      if (!container || isUpdatingZoom.current) return;

      for (const entry of entries) {
        const width = Math.round(entry.contentRect.width);
        const previousWidth = lastObservedContainerWidthRef.current;
        lastObservedContainerWidthRef.current = width;

        if (width > 100 && previousWidth !== null && width !== previousWidth) {
          scheduleAutoZoomRef.current(hasFinishedInitialLayoutRef.current ? 120 : 0);
        }
      }
    }, []);

    const hasFile = !!file;

    // Get optimal zoom when file loads. Keyed on document identity only —
    // see scheduleAutoZoomRef for why the zoom callbacks must not be deps.
    useEffect(() => {
      if (!containerRef.current || !hasFile) return;

      const container = containerRef.current;
      const resizeObserver = new ResizeObserver(handleResize);
      lastObservedContainerWidthRef.current = Math.round(container.clientWidth);
      resizeObserver.observe(container);

      // Initial zoom calculation
      if (container.clientWidth > 100) {
        scheduleAutoZoomRef.current(0);
      }

      return () => {
        resizeObserver.disconnect();
      };
    }, [hasFile, docId, fileUrl, handleResize]);

    // Add trackpad zoom functionality
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const handleWheel = (event: WheelEvent) => {
        // Check if this is a zoom gesture (Ctrl/Cmd key pressed or trackpad pinch)
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();

          // Calculate zoom delta based on wheel direction
          const delta = -event.deltaY * 0.01;
          handleZoom(delta);
        }
      };

      container.addEventListener('wheel', handleWheel, { passive: false });

      return () => {
        container.removeEventListener('wheel', handleWheel);
      };
    }, [handleZoom]);

    const visiblePages = useVisiblePages(containerRef, numPages, getPageDimensions);
    const [debouncedVisiblePages] = useDebounce(visiblePages, 30);

    // O(1) lookup using precomputed positions instead of O(n) loop
    const getPagePosition = useCallback((pageNumber: number) => pagePositions[pageNumber] ?? 0, [pagePositions]);

    // Handle scrolling to a highlight when the scrollToHighlightId changes
    const calculateScrollPosition = useCallback(
      (highlight: BoundingBoxWithId) => {
        const pageDims = getPageDimensions(highlight.pageNumber);

        // Calculate the scroll position using the top and the page number
        // Subtract 15% of page height for padding above the highlight
        return highlight.top * pageDims.height + getPagePosition(highlight.pageNumber) - pageDims.height * 0.15;
      },
      [getPageDimensions, getPagePosition],
    );

    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const scrollToHighlightRef = useRef<(id: string) => boolean>(() => false);
    const lastAutoScrolledHighlightRef = useRef<string | null>(null);
    const lastHighlightedFactAvailableRef = useRef(false);

    // Scroll the container to a target offset, smoothly for short hops and
    // instantly for long ones (see SMOOTH_SCROLL_MAX_VIEWPORTS).
    const scrollContainerToTop = useCallback((top: number, left?: number) => {
      const container = containerRef.current;
      if (!container) return;
      const target = Math.max(0, top);
      const isLongJump = Math.abs(target - container.scrollTop) > container.clientHeight * SMOOTH_SCROLL_MAX_VIEWPORTS;
      container.scrollTo({
        top: target,
        ...(left !== undefined ? { left: Math.max(0, left) } : {}),
        behavior: isLongJump ? 'auto' : 'smooth',
      });
    }, []);

    // The zoom at which a highlight fills a comfortable share of the viewport.
    // Returns null when the current zoom is already close enough (large
    // highlights sit at the fit-to-width floor, so they return null and keep
    // the plain-scroll path).
    const getHighlightTargetZoom = useCallback(
      (highlight: BoundingBoxWithId) => {
        const container = containerRef.current;
        if (!container || container.clientWidth === 0 || container.clientHeight === 0) return null;

        const base = getBasePageDimensions(highlight.pageNumber);
        const highlightWidth = highlight.width * base.width;
        const highlightHeight = highlight.height * base.height;
        if (highlightWidth <= 0 || highlightHeight <= 0) return null;

        const fitZoom = Math.min(
          (container.clientWidth * HIGHLIGHT_ZOOM_WIDTH_SHARE) / highlightWidth,
          (container.clientHeight * HIGHLIGHT_ZOOM_HEIGHT_SHARE) / highlightHeight,
        );
        const targetZoom = Math.min(Math.max(fitZoom, getOptimalZoom()), HIGHLIGHT_ZOOM_MAX);

        // Compare against the rendered zoom (state, not zoomRef): callers can
        // run from a layout effect in the same commit as a zoom change, before
        // the effect that syncs zoomRef has fired.
        if (Math.abs(targetZoom - zoom) < zoom * HIGHLIGHT_ZOOM_MIN_CHANGE) return null;
        return targetZoom;
      },
      [getBasePageDimensions, getOptimalZoom, zoom],
    );

    // Scroll offsets that place a highlight slightly above the vertical centre
    // and at the horizontal centre of the viewport, at an arbitrary zoom (the
    // page layout for `targetZoom` may not have rendered yet).
    const calculateHighlightViewport = useCallback(
      (highlight: BoundingBoxWithId, targetZoom: number) => {
        const container = containerRef.current;
        if (!container) return null;

        const base = getBasePageDimensions(highlight.pageNumber);
        const pageHeight = base.height * targetZoom;
        const pageWidth = base.width * targetZoom;

        // Page positions scale with zoom except the fixed 10px inter-page gaps.
        const pageTop = (basePageOffsets[highlight.pageNumber] ?? 0) * targetZoom + (highlight.pageNumber - 1) * 10;

        const highlightTop = highlight.top * pageHeight + pageTop;
        const highlightHeight = highlight.height * pageHeight;
        const top =
          highlightTop - Math.max(0, (container.clientHeight - highlightHeight) * HIGHLIGHT_VIEWPORT_TOP_SHARE);

        // Pages are horizontally centred in the content area (see the page
        // components' left-1/2 -translate-x-1/2), so centre the highlight when
        // the zoomed content is wider than the viewport.
        const contentWidth = Math.max(getMaxPageWidth() * targetZoom + 40, container.clientWidth);
        const highlightCenterX = (contentWidth - pageWidth) / 2 + (highlight.left + highlight.width / 2) * pageWidth;
        const left = highlightCenterX - container.clientWidth / 2;

        return { top: Math.max(0, top), left: Math.max(0, left) };
      },
      [getBasePageDimensions, basePageOffsets, getMaxPageWidth],
    );

    // A highlight can be split into several boxes with the same id on one page
    // (e.g. per line). Zoom and scroll target their union so we frame the whole
    // highlighted region, not just its first line.
    const getHighlightUnionBox = useCallback(
      (highlight: BoundingBoxWithId): BoundingBoxWithId => {
        const boxes = allHighlights.filter((h) => h.id === highlight.id && h.pageNumber === highlight.pageNumber);
        if (boxes.length <= 1) return highlight;

        const left = Math.min(...boxes.map((b) => b.left));
        const top = Math.min(...boxes.map((b) => b.top));
        const right = Math.max(...boxes.map((b) => b.left + b.width));
        const bottom = Math.max(...boxes.map((b) => b.top + b.height));
        return { ...highlight, left, top, width: right - left, height: bottom - top };
      },
      [allHighlights],
    );

    // Navigate to a highlight: zoom in first when it would otherwise be too
    // small to read (see getHighlightTargetZoom), otherwise plain-scroll.
    const navigateToHighlightBox = useCallback(
      (highlight: BoundingBoxWithId) => {
        const unionBox = getHighlightUnionBox(highlight);

        const targetZoom = getHighlightTargetZoom(unionBox);
        if (targetZoom !== null) {
          // Applied by the layout effect once the new zoom has rendered.
          pendingScrollRef.current = calculateHighlightViewport(unionBox, targetZoom);
          setZoom(targetZoom);
          return;
        }

        const viewport = calculateHighlightViewport(unionBox, zoom);
        if (viewport) scrollContainerToTop(viewport.top, viewport.left);
      },
      [getHighlightUnionBox, getHighlightTargetZoom, calculateHighlightViewport, scrollContainerToTop, zoom],
    );

    const scrollHighlightNow = useCallback(
      (id: string) => {
        const container = containerRef.current;
        if (!container) return false;

        const highlight = allHighlights.find((h) => h.id === id);
        if (!highlight) return false;

        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        if (setHighlightedFactId) {
          setHighlightedFactId(id);
        }

        navigateToHighlightBox(highlight);
        return true;
      },
      [allHighlights, navigateToHighlightBox, setHighlightedFactId],
    );

    const scrollToHighlight = useCallback(
      (id: string) => {
        if (!containerRef.current) return false;

        const highlight = allHighlights.find((h) => h.id === id);
        if (!highlight) return false;

        // Debounce scroll requests so repeated updates (for example during resize)
        // collapse into a single final scroll position.
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = setTimeout(() => {
          if (setHighlightedFactId) {
            setHighlightedFactId(id);
          }

          navigateToHighlightBox(highlight);
        }, 25);
        return true;
      },
      [navigateToHighlightBox, setHighlightedFactId, allHighlights],
    );

    useEffect(() => {
      scrollToHighlightRef.current = scrollToHighlight;
    }, [scrollToHighlight]);

    useLayoutEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      if (pendingScrollRef.current) {
        container.scrollTop = pendingScrollRef.current.top;
        container.scrollLeft = pendingScrollRef.current.left;
        pendingScrollRef.current = null;
      }

      if (pendingAutoZoomLayoutRef.current) {
        pendingAutoZoomLayoutRef.current = false;
        // Don't latch the initial-layout flag until we actually know how many
        // pages the doc has — otherwise scrollHighlightNow scrolls against an
        // empty pagePositions array and pins us at the wrong spot.
        if (!hasFinishedInitialLayoutRef.current && numPages > 0) {
          if (!highlightedFactId || scrollHighlightNow(highlightedFactId)) {
            lastAutoScrolledHighlightRef.current = highlightedFactId ?? null;
            lastHighlightedFactAvailableRef.current = hasHighlightedFact;
            hasFinishedInitialLayoutRef.current = true;
          }
        } else if (pendingAutoZoomHighlightSyncRef.current && highlightedFactId) {
          if (scrollHighlightNow(highlightedFactId)) {
            lastAutoScrolledHighlightRef.current = highlightedFactId;
            lastHighlightedFactAvailableRef.current = hasHighlightedFact;
          }
        }
        pendingAutoZoomHighlightSyncRef.current = false;
      }
    }, [zoom, highlightedFactId, hasHighlightedFact, scrollHighlightNow, numPages]);

    useEffect(() => {
      if (hasFinishedInitialLayoutRef.current) return;
      if (pendingAutoZoomLayoutRef.current) return;
      // Wait until the file is ready *and* we know the page count. For
      // fileUrl-driven viewing `doc` is undefined, so we can't use `doc?.file`
      // as the readiness signal — `file` (the resolved metadata) plus a
      // non-zero `numPages` covers both modes.
      if (!file || numPages === 0) return;

      if (!highlightedFactId || scrollHighlightNow(highlightedFactId)) {
        lastAutoScrolledHighlightRef.current = highlightedFactId ?? null;
        lastHighlightedFactAvailableRef.current = hasHighlightedFact;
        hasFinishedInitialLayoutRef.current = true;
      }
    }, [file, numPages, highlightedFactId, hasHighlightedFact, scrollHighlightNow]);

    // Scroll only when the requested highlight changes or when it first becomes available.
    // Manual zoom updates should preserve the user's viewport instead of jumping back here.
    useEffect(() => {
      if (!hasFinishedInitialLayoutRef.current) return;
      if (!highlightedFactId) {
        lastAutoScrolledHighlightRef.current = null;
        lastHighlightedFactAvailableRef.current = false;
        return;
      }

      const highlightChanged = lastAutoScrolledHighlightRef.current !== highlightedFactId;
      const highlightBecameAvailable = hasHighlightedFact && !lastHighlightedFactAvailableRef.current;
      lastHighlightedFactAvailableRef.current = hasHighlightedFact;

      if (!hasHighlightedFact || (!highlightChanged && !highlightBecameAvailable)) return;

      if (scrollToHighlightRef.current(highlightedFactId)) {
        lastAutoScrolledHighlightRef.current = highlightedFactId;
      }
    }, [highlightedFactId, hasHighlightedFact]);

    useEffect(() => {
      hasFinishedInitialLayoutRef.current = false;
      pendingAutoZoomLayoutRef.current = false;
      pendingAutoZoomHighlightSyncRef.current = false;
      pendingScrollRef.current = null;
      lastObservedContainerWidthRef.current = null;
      lastAutoScrolledHighlightRef.current = null;
      lastHighlightedFactAvailableRef.current = false;

      if (resizeAutoZoomTimeoutRef.current) {
        clearTimeout(resizeAutoZoomTimeoutRef.current);
        resizeAutoZoomTimeoutRef.current = null;
      }

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      setLoadedFileUrlPageCount(null);
      setLoadedPdfPageDimensions({});
    }, [docId, fileUrl]);

    useEffect(() => {
      return () => {
        if (resizeAutoZoomTimeoutRef.current) {
          clearTimeout(resizeAutoZoomTimeoutRef.current);
        }
      };
    }, []);

    // Navigate to the next highlight within the current fact's pages (loops around)
    const handleNextFactHighlight = useCallback(() => {
      if (uniquePagesForCurrentFact.length === 0) return;

      const nextIndex = (currentFactHighlightIndex + 1) % uniquePagesForCurrentFact.length;
      setCurrentFactHighlightIndex(nextIndex);

      // Find the first highlight on the target page
      const targetPage = uniquePagesForCurrentFact[nextIndex];
      const targetHighlight = factHighlightsForCurrentFact.find((h) => h.pageNumber === targetPage);

      if (targetHighlight && containerRef.current) {
        navigateToHighlightBox(targetHighlight);
      }
    }, [uniquePagesForCurrentFact, currentFactHighlightIndex, factHighlightsForCurrentFact, navigateToHighlightBox]);

    // Navigate to the previous highlight within the current fact's pages (loops around)
    const handlePreviousFactHighlight = useCallback(() => {
      if (uniquePagesForCurrentFact.length === 0) return;

      const prevIndex =
        currentFactHighlightIndex <= 0 ? uniquePagesForCurrentFact.length - 1 : currentFactHighlightIndex - 1;
      setCurrentFactHighlightIndex(prevIndex);

      // Find the first highlight on the target page
      const targetPage = uniquePagesForCurrentFact[prevIndex];
      const targetHighlight = factHighlightsForCurrentFact.find((h) => h.pageNumber === targetPage);

      if (targetHighlight && containerRef.current) {
        navigateToHighlightBox(targetHighlight);
      }
    }, [uniquePagesForCurrentFact, currentFactHighlightIndex, factHighlightsForCurrentFact, navigateToHighlightBox]);

    const handleSelectionComplete = useCallback(
      (boundingBox: BoundingBox, imageData: string, selectionBox: SelectionBox, pageNumber: number) => {
        const pageElement = document.querySelector(`[data-page-number="${pageNumber}"]`);

        if (pageElement && containerRef.current) {
          const pageRect = pageElement.getBoundingClientRect();
          const containerRect = containerRef.current.getBoundingClientRect();

          // Determine side based on PDF viewer position relative to viewport
          const viewportWidth = window.innerWidth;
          const pdfViewerLeftEdge = containerRect.left;
          const isLeftHalf = pdfViewerLeftEdge < viewportWidth * 0.5;
          const side = isLeftHalf ? 'right' : 'left';

          // Position anchor based on determined side
          const anchorX =
            side === 'right'
              ? pageRect.left - containerRect.left + selectionBox.endX // Right edge of selection
              : pageRect.left - containerRect.left + selectionBox.startX; // Left edge of selection

          const anchorY =
            pageRect.top - containerRect.top + selectionBox.startY + (selectionBox.endY - selectionBox.startY) / 2;

          setPopupData({
            isOpen: true,
            imageBase64: imageData,
            boundingBox,
            anchorPosition: { x: anchorX, y: anchorY, pageNumber },
            side,
          });
          setActiveSelection({ box: selectionBox, pageNumber });
          setIsSelectionMode(false);
        }
      },
      [],
    );

    const handlePopupClose = useCallback(() => {
      setPopupData({
        isOpen: false,
        imageBase64: '',
        boundingBox: null,
        anchorPosition: null,
        side: 'left',
      });
      setActiveSelection(null);
    }, []);

    const handleFactExtractionSuccess = useCallback(() => {
      // Refresh facts or show success message
      handlePopupClose();
    }, [handlePopupClose]);

    // Search functions
    const handleSearch = useCallback((query: string) => {
      trackCustomEvent(TrackingEventsV2.DocViewerSearch);
      setSearchQuery(query);
      setCurrentMatchIndex(0);
      setFocusedMatch(null);
      setLazyMatch(null);
      hasUserNavigatedRef.current = false;
    }, []);

    // Scroll a given page so a normalised (0..1) vertical position sits near the
    // top of the viewport (15% of page height of padding above it).
    const scrollToPagePosition = useCallback(
      (pageNumber: number, normalizedTop: number) => {
        const container = containerRef.current;
        if (!container) return;
        const pageDims = getPageDimensions(pageNumber);
        const scrollY = normalizedTop * pageDims.height + getPagePosition(pageNumber) - pageDims.height * 0.15;
        scrollContainerToTop(scrollY);
      },
      [getPageDimensions, getPagePosition],
    );

    // Determine the page currently centred in the viewport, so a new search can
    // jump to the nearest match rather than always the first one.
    const getViewportAnchorPage = useCallback(() => {
      const container = containerRef.current;
      if (!container || numPages === 0) return 1;

      const anchorY = container.scrollTop + container.clientHeight / 2;
      let page = 1;
      for (let i = 1; i <= numPages; i++) {
        if (pagePositions[i] <= anchorY) page = i;
        else break;
      }
      return page;
    }, [numPages, pagePositions]);

    // Index of the search match whose page is closest to the page in view.
    const getNearestMatchIndex = useCallback(() => {
      const anchorPage = getViewportAnchorPage();
      let bestIndex = 0;
      let bestDistance = Infinity;

      if (useTextLayer) {
        wordMatches.forEach((match, index) => {
          const distance = Math.abs(match.pageNumber - anchorPage);
          if (distance < bestDistance) {
            bestDistance = distance;
            bestIndex = index;
          }
        });
        return bestIndex;
      }

      matchOccurrences.forEach((chunk, index) => {
        const firstBox = allHighlights.find((h) => h.id === chunk.boundingBoxIds[0]);
        if (!firstBox) return;

        const distance = Math.abs(firstBox.pageNumber - anchorPage);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      });

      return bestIndex;
    }, [useTextLayer, wordMatches, matchOccurrences, allHighlights, getViewportAnchorPage]);

    const navigateToMatch = useCallback(
      (targetIndex: number) => {
        if (totalSearchMatches === 0 || targetIndex < 0 || targetIndex >= totalSearchMatches) return;
        setCurrentMatchIndex(targetIndex);

        if (useTextLayer) {
          const match = wordMatches[targetIndex];
          if (!match) return;
          setFocusedMatch({ pageNumber: match.pageNumber, indexOnPage: match.indexOnPage });
          scrollToPagePosition(match.pageNumber, match.top);
          return;
        }

        // Otherwise: scroll to the first bounding box of the match's chunk.
        const currentChunk = matchOccurrences[targetIndex];
        if (currentChunk && currentChunk.boundingBoxIds.length > 0) {
          setHighlightedChunkId(currentChunk.chunkId);
          const firstBox = allHighlights.find((h) => h.id === currentChunk.boundingBoxIds[0]);
          if (firstBox && containerRef.current) {
            const scrollY = calculateScrollPosition(firstBox);
            scrollContainerToTop(scrollY);
          }
        }
      },
      [
        totalSearchMatches,
        useTextLayer,
        wordMatches,
        scrollToPagePosition,
        matchOccurrences,
        allHighlights,
        calculateScrollPosition,
      ],
    );

    // --- Lazy search navigation ---
    // Resolve the next/previous match on demand by walking the candidate page
    // list from `current` fetching each page's text only as we reach it and skipping pages
    // with no literal match. Returns null at the document's first/last match (no wrap-around in lazy
    // mode — we can't know the far end without scanning everything).
    const resolveLazyMatch = useCallback(
      async (current: LazySearchMatch | null, direction: 1 | -1): Promise<LazySearchMatch | null> => {
        const pages = candidateSearchPageIndices;
        const query = normaliseSearchQuery(searchQuery);
        if (!query || pages.length === 0) return null;

        let pageListIndex = current ? current.pageListIndex : direction === 1 ? 0 : pages.length - 1;
        // On the page we start from, continue from the adjacent match; on every
        // page after that, take the leading match in the direction of travel.
        let withinPageStart: number | null = current ? current.matchIndexOnPage + direction : null;

        while (pageListIndex >= 0 && pageListIndex < pages.length) {
          const pageIndex = pages[pageListIndex];
          const lines = await fetchLazyPageText(pageIndex);
          const matches = findPageMatches(lines, query);

          const idx = withinPageStart ?? (direction === 1 ? 0 : matches.length - 1);
          withinPageStart = null; // only applies to the first page examined

          if (idx >= 0 && idx < matches.length) {
            const box = matchAnchorBox(lines, matches[idx]);
            return {
              ordinal: current ? Math.max(1, current.ordinal + direction) : 1,
              pageListIndex,
              matchIndexOnPage: idx,
              pageNumber: pageIndex + 1,
              top: box?.top ?? 0,
            };
          }
          pageListIndex += direction;
        }
        return null;
      },
      [candidateSearchPageIndices, searchQuery, fetchLazyPageText],
    );

    // Prefetch the candidate pages either side so the next hop is instant.
    const prefetchLazyNeighbours = useCallback(
      (pageListIndex: number) => {
        const pages = candidateSearchPageIndices;
        [pageListIndex - 1, pageListIndex + 1].forEach((i) => {
          if (i >= 0 && i < pages.length) void fetchLazyPageText(pages[i]);
        });
      },
      [candidateSearchPageIndices, fetchLazyPageText],
    );

    const applyLazyMatch = useCallback(
      (match: LazySearchMatch) => {
        setLazyMatch(match);
        setFocusedMatch({ pageNumber: match.pageNumber, indexOnPage: match.matchIndexOnPage });
        scrollToPagePosition(match.pageNumber, match.top);
        prefetchLazyNeighbours(match.pageListIndex);
      },
      [scrollToPagePosition, prefetchLazyNeighbours],
    );

    const stepLazy = useCallback(
      async (direction: 1 | -1) => {
        hasUserNavigatedRef.current = true;
        setIsNavigatingLazy(true);
        try {
          const next = await resolveLazyMatch(lazyMatch, direction);
          if (next) applyLazyMatch(next);
        } finally {
          setIsNavigatingLazy(false);
        }
      },
      [resolveLazyMatch, lazyMatch, applyLazyMatch],
    );

    const handleNextMatch = useCallback(() => {
      if (isLazySearch) {
        void stepLazy(1);
        return;
      }
      if (totalSearchMatches > 0) {
        hasUserNavigatedRef.current = true;
        const safeCurrentIndex = Math.min(currentMatchIndex, totalSearchMatches - 1);
        const nextIndex = (safeCurrentIndex + 1) % totalSearchMatches;
        navigateToMatch(nextIndex);
      }
    }, [isLazySearch, stepLazy, currentMatchIndex, totalSearchMatches, navigateToMatch]);

    const handlePreviousMatch = useCallback(() => {
      if (isLazySearch) {
        void stepLazy(-1);
        return;
      }
      if (totalSearchMatches > 0) {
        hasUserNavigatedRef.current = true;
        const safeCurrentIndex = Math.min(currentMatchIndex, totalSearchMatches - 1);
        const prevIndex = safeCurrentIndex === 0 ? totalSearchMatches - 1 : safeCurrentIndex - 1;
        navigateToMatch(prevIndex);
      }
    }, [isLazySearch, stepLazy, currentMatchIndex, totalSearchMatches, navigateToMatch]);

    const handleCloseSearch = useCallback(() => {
      setShowSearchBar(false);
      setSearchQuery('');
      setCurrentMatchIndex(0);
      setHighlightedChunkId(null);
      setFocusedMatch(null);
      setLazyMatch(null);
      setIsNavigatingLazy(false);
      hasUserNavigatedRef.current = false;
    }, []);

    // When matches first load, jump to the result nearest the page currently in
    // view rather than scrolling back to the first occurrence. Only until the
    // user starts stepping through results themselves — after that, navigation
    // (including looping from the last match back to the first) is left alone.
    useEffect(() => {
      if (!isLazySearch && searchQuery && totalSearchMatches > 0 && !hasUserNavigatedRef.current) {
        navigateToMatch(getNearestMatchIndex());
      }
    }, [isLazySearch, searchQuery, totalSearchMatches, navigateToMatch, getNearestMatchIndex]);

    // Lazy mode: land on the first match once the candidate pages are known.
    // (Starting at the top keeps the displayed ordinal exact — see LazySearchMatch.)
    useEffect(() => {
      if (!isLazySearch || !searchQuery) return;
      if (candidateSearchPageIndices.length === 0) return;
      if (lazyMatch !== null || hasUserNavigatedRef.current) return;

      let cancelled = false;
      setIsNavigatingLazy(true);
      resolveLazyMatch(null, 1)
        .then((first) => {
          if (!cancelled && first) applyLazyMatch(first);
        })
        .finally(() => {
          if (!cancelled) setIsNavigatingLazy(false);
        });
      return () => {
        cancelled = true;
      };
    }, [isLazySearch, searchQuery, candidateSearchPageIndices, lazyMatch, resolveLazyMatch, applyLazyMatch]);

    // Expose clearSearch method to parent
    useImperativeHandle(
      ref,
      () => ({
        clearSearch: () => {
          handleCloseSearch();
        },
      }),
      [handleCloseSearch],
    );

    if (!matterId) {
      return null;
    }

    return (
      <>
        <div className="absolute top-0 right-0 left-0 z-30 m-2.5 flex items-start justify-between gap-1.5">
          <div className="flex flex-wrap gap-1.5">
            <Zoom handleZoomOut={handleZoomOut} handleZoomIn={handleZoomIn} />
            {!fileUrl && (
              <SearchButton showSearchBar={showSearchBar} onClick={() => setShowSearchBar(!showSearchBar)} />
            )}
            {hasBeenConvertedToImages && !hideSnippingTool && (
              <ExtractFactsFromSelectionButton
                isSelectionMode={isSelectionMode}
                setIsSelectionMode={setIsSelectionMode}
                docId={docId}
              />
            )}
            {!hideDownload && (
              <DownloadIcon
                matterId={matterId}
                docId={docId}
                fileName={fileName}
                isProcessed={doc?.isProcessed}
                fileExtension={file?.extension}
                threadFile={threadFile}
              />
            )}
            {!hideFeatureButtons && showFacts !== undefined && setShowFacts !== undefined && (
              <>
                <FullDocLink docId={docId} />
                <ShowFactsIcon showFacts={showFacts} setShowFacts={setShowFacts} />
              </>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2.5">
            <AnimatePresence mode="wait">
              {showSearchBar && !fileUrl ? (
                <motion.div
                  key="search-bar"
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.15 }}
                >
                  <SearchBar
                    onSearch={handleSearch}
                    onNext={handleNextMatch}
                    onPrevious={handlePreviousMatch}
                    onClose={handleCloseSearch}
                    hasResults={matchCounter.hasResults}
                    currentMatch={matchCounter.current}
                    totalMatches={matchCounter.total}
                    isLoading={isSearchLoading || isNavigatingLazy}
                    initialQuery={initialSearchQuery}
                  />
                </motion.div>
              ) : highlightedFactId && !hideFactInfo ? (
                <motion.div
                  key="fact-info"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.15 }}
                  className="flex items-center gap-2 rounded-lg border border-gray-300 bg-gray-50 p-1.5 pl-3 text-xs text-gray-700 shadow-lg/3"
                >
                  <Icon className="lucide--info text-teal-500" />
                  <div className="whitespace-nowrap">
                    Fact sourced from <span className="font-medium">{uniquePagesForCurrentFact.length}</span>{' '}
                    {uniquePagesForCurrentFact.length === 1 ? 'page' : 'pages'}{' '}
                    <span className="text-gray-400">
                      ({currentFactHighlightIndex + 1}/{uniquePagesForCurrentFact.length})
                    </span>
                  </div>

                  <div>
                    <ButtonGroup>
                      <Button
                        size="iconSm"
                        className="bg-white hover:border-gray-400 hover:bg-white"
                        onClick={handlePreviousFactHighlight}
                      >
                        <Icon className="lucide--arrow-up" />
                      </Button>
                      <Button
                        size="iconSm"
                        className="bg-white hover:border-gray-400 hover:bg-white"
                        onClick={handleNextFactHighlight}
                      >
                        <Icon className="lucide--arrow-down" />
                      </Button>
                    </ButtonGroup>
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>

            {closeViewer && <CloseIcon onClick={closeViewer} className="relative m-0" />}
          </div>
        </div>

        <div
          ref={containerRef}
          className="relative h-full overflow-auto bg-gray-100 leading-[0]"
          onClick={onViewerClick}
        >
          <div className="inline-block min-h-full min-w-full bg-gray-100 pt-5 pb-3">
            {isDocLoading ? (
              <DocViewerLoader />
            ) : (
              <>
                {hasBeenConvertedToImages ? (
                  <>
                    <ImageRenderer
                      docId={docId}
                      getPageDimensions={getPageDimensions}
                      getBasePageDimensions={getBasePageDimensions}
                      zoom={zoom}
                      visiblePages={debouncedVisiblePages}
                      highlights={allHighlights}
                      getPagePosition={getPagePosition}
                      highlightedFactId={highlightedFactId}
                      onHighlightClick={onHighlightClick}
                      highlightedChunkId={highlightedChunkId}
                      searchQuery={searchQuery}
                      focusedMatch={focusedMatch}
                      totalHeight={totalHeight}
                      maxPageWidth={maxPageWidth}
                      isSelectionMode={isSelectionMode}
                      onSelectionComplete={handleSelectionComplete}
                      maintainSelection={popupData.isOpen}
                      activeSelection={activeSelection}
                    />
                  </>
                ) : (
                  <PDFDocumentRenderer
                    docId={docId}
                    getPageDimensions={getPageDimensions}
                    visiblePages={visiblePages}
                    highlights={allHighlights}
                    getPagePosition={getPagePosition}
                    highlightedFactId={highlightedFactId}
                    onHighlightClick={onHighlightClick}
                    highlightedChunkId={highlightedChunkId}
                    totalHeight={totalHeight}
                    maxPageWidth={maxPageWidth}
                    zoom={zoom}
                    fileUrl={fileUrl}
                    onDocumentLoadSuccess={setLoadedFileUrlPageCount}
                    onPageLoadSuccess={handlePdfPageLoadSuccess}
                  />
                )}
              </>
            )}
          </div>
        </div>
        {popupData.isOpen && popupData.boundingBox && popupData.anchorPosition && (
          <div
            style={{
              position: 'absolute',
              left: popupData.anchorPosition.x,
              top: popupData.anchorPosition.y,
              width: 1,
              height: 1,
              pointerEvents: 'none',
              zIndex: 50,
            }}
          >
            <ExtractFactFromImagePopup
              isOpen={popupData.isOpen}
              onClose={handlePopupClose}
              docId={docId}
              imageBase64={popupData.imageBase64}
              boundingBox={popupData.boundingBox}
              onSuccess={handleFactExtractionSuccess}
              side={popupData.side}
            />
          </div>
        )}
      </>
    );
  },
);
